import { useBlocker } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'

import { refusedBecause } from '~/api/client'
import { Working } from '~/components/working'
import { useBeat } from '~/components/use-work'
import { ConfirmDialog } from '~/components/confirm-dialog'
import { PanelHeader } from '~/components/panel-header'
import { UploadIcon } from '~/components/icons'
import { useToast } from '~/components/toast-context'
import { useApi } from '~/data/data-context'
import {
  PHOTO_TYPES,
  PHOTO_TYPE_LABELS,
  photoName,
  type WeighInPhoto,
  type PhotoType,
} from '~/data/weighins'
import { dayStart, formatDayLabel } from '~/lib/dates'

/**
 * A progress photo arriving in two steps, morphed into the corner card: first what it is of —
 * a pill per type, with "something else" earning a label of its own — then the photo itself,
 * dragged in or browsed for, which uploads the moment it lands. Done, the card morphs back to
 * the day's photos.
 */
export function WeighInAddPhotoPanel({
  day,
  onAdded,
  onBack,
}: {
  /** The day the photo lands on, inherited from the photos panel it was opened from. */
  day: string
  /** The photo is on its day — the card returns to the photos panel. */
  onAdded: (photo: WeighInPhoto) => void
  onBack: () => void
}) {
  const api = useApi()
  const toast = useToast()

  // The chosen type is what moves the panel to its second step.
  const [chosen, setChosen] = useState<{ type: PhotoType; label: string | null } | null>(null)
  // The custom pill selected: the label field shows, and Next is what moves on.
  const [naming, setNaming] = useState(false)
  const [customLabel, setCustomLabel] = useState('')
  const [dragging, setDragging] = useState(false)
  const [uploading, setUploading] = useState(false)
  const uploadingShown = useBeat(uploading)
  const fileInput = useRef<HTMLInputElement>(null)

  // The label field is what the custom pill reveals, so it opens ready to be typed into.
  const labelField = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (naming) {
      labelField.current?.focus()
    }
  }, [naming])

  /*
   * A typed label is the only thing here that would be lost — a picked pill is one click.
   * Every way out is a navigation, so the guard sits on the navigation, as feedback's does.
   */
  const holding = useRef(false)
  useEffect(() => {
    holding.current = customLabel.trim() !== '' && !uploading
  }, [customLabel, uploading])

  const leaving = useBlocker({
    shouldBlockFn: ({ next }) => holding.current && next.search.dialog !== 'weighin-add-photo',
    enableBeforeUnload: false,
    withResolver: true,
  })

  const upload = async (file: File) => {
    if (chosen === null || uploading) {
      return
    }
    if (!file.type.startsWith('image/')) {
      toast('That is not an image.')
      return
    }
    setUploading(true)
    try {
      const id = crypto.randomUUID()
      await api.putWeighInPhoto(day, id, chosen.type, chosen.label)
      const photo = await api.uploadWeighInPhoto(id, file)
      holding.current = false
      onAdded(photo)
    } catch (trouble) {
      /*
       * Out of room is not a moment's trouble: the account's plan has no space left, and
       * trying again in a moment is advice that can never come good. It says what happened
       * and where to do something about it instead.
       */
      toast(
        refusedBecause(trouble, 'out-of-room')
          ? 'No room left in your plan. Free some up, or see your plan in settings.'
          : 'The photo did not reach us. Try again in a moment.',
      )
    } finally {
      setUploading(false)
    }
  }

  return (
    <>
      {chosen === null ? (
        <>
          <PanelHeader title="What is this photo of?" onBack={onBack} />
          <p className="px-4 pb-2 text-sm text-ink-muted">{formatDayLabel(dayStart(day))}</p>
          <div className="w-80 space-y-3 px-4 pb-4">
            <div className="flex flex-wrap gap-2">
              {PHOTO_TYPES.map((type) => (
                <button
                  key={type}
                  type="button"
                  className="rounded-full px-4 py-2 text-sm ring-1 ring-white/15 transition-colors hover:bg-hover"
                  onClick={() => {
                    setChosen({ type, label: null })
                  }}
                >
                  {PHOTO_TYPE_LABELS[type]}
                </button>
              ))}
              <button
                type="button"
                aria-pressed={naming}
                className={`rounded-full px-4 py-2 text-sm ring-1 transition-colors ${
                  naming
                    ? 'bg-accent/15 text-accent ring-accent/40'
                    : 'ring-white/15 hover:bg-hover'
                }`}
                onClick={() => {
                  setNaming((held) => !held)
                }}
              >
                Something else
              </button>
            </div>
            {naming && (
              <div className="flex items-center gap-2">
                <input
                  ref={labelField}
                  type="text"
                  aria-label="What the photo is of"
                  placeholder="What is it of?"
                  value={customLabel}
                  onChange={(event) => {
                    setCustomLabel(event.target.value)
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && customLabel.trim() !== '') {
                      setChosen({ type: 'custom', label: customLabel.trim() })
                    }
                  }}
                  className="w-full rounded-2xl bg-surface p-3 text-sm outline-none ring-1 ring-white/10 placeholder:text-ink-muted focus:ring-2 focus:ring-accent"
                />
                <button
                  type="button"
                  disabled={customLabel.trim() === ''}
                  className="shrink-0 rounded-full bg-accent px-4 py-2 text-sm font-medium text-on-accent disabled:opacity-50"
                  onClick={() => {
                    setChosen({ type: 'custom', label: customLabel.trim() })
                  }}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          <PanelHeader
            title={`Add the ${photoName(chosen).toLowerCase()} photo`}
            backLabel="Back to photo type"
            onBack={() => {
              if (!uploading) {
                setChosen(null)
              }
            }}
          />
          <p className="px-4 pb-2 text-sm text-ink-muted">{formatDayLabel(dayStart(day))}</p>
          <div className="w-80 px-4 pb-4">
            <button
              type="button"
              aria-busy={uploading || undefined}
              className={`relative flex h-40 w-full flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed text-sm transition-colors ${
                dragging
                  ? 'border-accent bg-hover text-ink'
                  : 'border-white/15 text-ink-muted hover:bg-hover'
              }`}
              onClick={() => {
                if (!uploading) {
                  fileInput.current?.click()
                }
              }}
              onDragOver={(event) => {
                event.preventDefault()
                if (!uploading) {
                  setDragging(true)
                }
              }}
              onDragLeave={() => {
                setDragging(false)
              }}
              onDrop={(event) => {
                event.preventDefault()
                setDragging(false)
                const file = event.dataTransfer.files[0]
                if (file !== undefined) {
                  void upload(file)
                }
              }}
            >
              <Working shown={uploadingShown} spinner="size-8">
                <UploadIcon aria-hidden className="size-8" />
                Drag a photo here, or click to browse
              </Working>
            </button>
            <input
              ref={fileInput}
              type="file"
              accept="image/*"
              aria-label="Progress photo file"
              className="sr-only"
              onChange={(event) => {
                const file = event.target.files?.[0]
                if (file !== undefined) {
                  void upload(file)
                }
                event.target.value = ''
              }}
            />
          </div>
        </>
      )}

      {leaving.status === 'blocked' && (
        <ConfirmDialog
          title="Discard this photo?"
          body="The label you have written is not saved."
          confirm="Discard"
          onConfirm={() => {
            holding.current = false
            leaving.proceed()
          }}
          onDismiss={leaving.reset}
        />
      )}
    </>
  )
}
